import { observer } from 'mobx-react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';

import FancyPopup from '@/common/components/FancyPopup/FancyPopup';
import Icon from '@/common/components/Icon/Icon';
import Link, { NONE } from '@/common/components/Link/Link';
import MenuItem from '@/common/components/MenuList/MenuItem/MenuItem';
import MenuList from '@/common/components/MenuList/MenuList';
import ProductPlaceholder, {
  COLOR_OUTLINE,
  COLOR_OUTLINE_GREEN,
} from '@/media/components/ProductPlaceholder/ProductPlaceholder';
import { SHAPE_PEAR, SHAPE_ROUND } from '@/product/entity/CutShape';

import styles from '../Header.module.css';

export interface LabGrownDiamondsNavItemProps {
  title: string;
  isActive: boolean;
  colorlessLink: string;
  fancyLink: string;
  roughLink: string;
  navClassName?: string;
}

function DiamondsNavItem(props: LabGrownDiamondsNavItemProps) {
  const { title, isActive, colorlessLink, navClassName, fancyLink, roughLink } = props;

  const [isOpen, open, close] = useToggle();

  function onChangeMenuState(isNextOpened: boolean) {
    if (isNextOpened) {
      open();
    } else {
      close();
    }
  }

  return (
    <FancyPopup
      action="click"
      isOpen={isOpen}
      onChangeState={onChangeMenuState}
      onClickOutside={close}
      placement="bottom"
      usePortal
      isPlain
      body={(
        <MenuList withRoundedCorners>
          <MenuItem className={styles.headerProductTypeMenuWrapper}>
            <Link
              aria-label="Polished Colorless"
              activeClassName={styles.active}
              appearance={NONE}
              className={styles.headerProductTypeMenuItem}
              exact
              onClick={close}
              role="menuitem"
              to={colorlessLink}
            >
              <div className={styles.headerProductTypeIcon}>
                <ProductPlaceholder bold color={COLOR_OUTLINE} shape={SHAPE_ROUND} />
              </div>
              Polished Colorless
            </Link>
          </MenuItem>

          <MenuItem className={styles.headerProductTypeMenuWrapper}>
            <Link
              aria-label="Polished Fancy Color"
              activeClassName={styles.active}
              appearance={NONE}
              className={styles.headerProductTypeMenuItem}
              exact
              onClick={close}
              role="menuitem"
              to={fancyLink}
            >
              <div className={styles.headerProductTypeIcon}>
                <ProductPlaceholder bold color={COLOR_OUTLINE_GREEN} shape={SHAPE_PEAR} />
              </div>
              Polished Fancy Color
            </Link>
          </MenuItem>

          <MenuItem className={styles.headerProductTypeMenuWrapper}>
            <Link
              aria-label="Rough"
              activeClassName={styles.active}
              appearance={NONE}
              className={styles.headerProductTypeMenuItem}
              exact
              onClick={close}
              role="menuitem"
              to={roughLink}
            >
              <div className={styles.headerProductTypeIcon}>
                <ProductPlaceholder bold color={COLOR_OUTLINE} shape="rough" />
              </div>
              Rough
            </Link>
          </MenuItem>
        </MenuList>
      )}
      trigger={(
        <div
          aria-label={title}
          className={cls(
            styles.headerNavLink,
            styles.headerNavLinkDesktop,
            styles.headerNavLinkDropdown,
            navClassName,
            { [styles.headerLinkActive]: isActive },
          )}
        >
          <span>{title} </span>

          <Icon
            className={cls(styles.headerNavLinkTriangle, { [styles.headerNavLinkTriangleOpened]: isOpen })}
            name="arrow-lt-down"
          />
        </div>
      )}
    />
  );
}

export default observer(DiamondsNavItem);
